import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../../context/FilterContext';
import { useResponsive } from '../../hooks/useResponsive';
import './DateRangeFilter.css';

const DateRangeFilter: React.FC = () => {
  const { t } = useTranslation();
  const { tempFilters, setDateRange, applyTempFilters, cancelTempFilters, hasUnappliedChanges } = useFilters();
  const [showPicker, setShowPicker] = useState(false);
  const { isLaptop, isiPad, isiPhone } = useResponsive();

  const dateRange = tempFilters.dateRange; 

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateRange({
      ...dateRange,
      startDate: e.target.value
    });
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateRange({
      ...dateRange,
      endDate: e.target.value
    });
  };

  const handleQuickSelect = (days: number) => {
    const [year, month, day] = dateRange.endDate.split('-');
    const endDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - days + 1); 

    setDateRange({
      startDate: startDate.toISOString().split('T')[0],
      endDate: dateRange.endDate 
    });
  };

  const formatDateForDisplay = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-');
    return `${year}/${parseInt(month)}/${parseInt(day)}`;
  };

  const dateTextStyles = {
    fontSize: isiPhone ? '0.85rem' : isiPad ? '0.9rem' : isLaptop ? '1.05rem' : '1.2rem',
    fontWeight: 700,
    color: 'white',
    letterSpacing: '0.3px',
    flex: 1,
    textAlign: 'center' as const,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const
  };

  return (
    <div className="date-range-filter">
      <button 
        className="filter-button date-filter-button"
        onClick={() => setShowPicker(!showPicker)}
        style={{
          fontSize: isLaptop ? '12px' : '14px',
          padding: isLaptop ? '6px 10px' : '8px 12px'
        }}
      >
        <span className="calendar-icon">📅</span>
        <span style={dateTextStyles}>
          {formatDateForDisplay(dateRange.startDate)} - {formatDateForDisplay(dateRange.endDate)}
        </span>
        <span className="dropdown-arrow">{showPicker ? '▲' : '▼'}</span>
      </button>

      {showPicker && (
        <div className="date-picker-dropdown" style={{
          padding: isLaptop ? '10px' : '15px',
          width: isLaptop ? '300px' : '350px'
        }}>
          <div className="quick-select-section" style={{
            marginBottom: isLaptop ? '10px' : '20px',
            paddingBottom: isLaptop ? '10px' : '20px'
          }}>
            <div className="quick-select-title" style={{ 
              fontSize: isLaptop ? '12px' : '15px',
              marginBottom: isLaptop ? '5px' : '8px'
            }}>
              {t('dateRange.quickSelect')}
            </div>
            <div className="quick-select-buttons" style={{
              gap: isLaptop ? '6px' : '10px'
            }}>
              <button 
                onClick={() => handleQuickSelect(14)} 
                style={{
                  fontSize: isLaptop ? '12px' : '13px',
                  padding: isLaptop ? '3px 6px' : '5px 10px',
                  margin: isLaptop ? '0 2px' : '0 4px'
                }}
              >
                {t('dateRange.last14Days')}
              </button>
              <button 
                onClick={() => handleQuickSelect(30)} 
                style={{
                  fontSize: isLaptop ? '12px' : '13px',
                  padding: isLaptop ? '3px 6px' : '5px 10px',
                  margin: isLaptop ? '0 2px' : '0 5px'
                }}
              >
                {t('dateRange.last30Days')}
              </button>
              <button 
                onClick={() => handleQuickSelect(90)} 
                style={{
                  fontSize: isLaptop ? '12px' : '13px',
                  padding: isLaptop ? '3px 6px' : '5px 10px',
                  margin: isLaptop ? '0 2px' : '0 5px'
                }}
              >
                {t('dateRange.last90Days')}
              </button>
            </div>
          </div>
          
          <div className="custom-date-section" style={{ 
            marginTop: isLaptop ? '8px' : '15px',
            marginBottom: isLaptop ? '10px' : '20px'
          }}>
            <div className="custom-date-title" style={{ 
              fontSize: isLaptop ? '12px' : '15px',
              marginBottom: isLaptop ? '5px' : '8px'
            }}>
              {t('dateRange.custom')}
            </div>
            <div className="date-inputs" style={{
              gap: isLaptop ? '10px' : '15px'
            }}>
              <div className="date-input-group">
                <label style={{ 
                  fontSize: isLaptop ? '12px' : '13px',
                  marginBottom: isLaptop ? '3px' : '6px'
                }}>
                  {t('dateRange.startDate')}
                </label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={handleStartDateChange}
                  style={{ 
                    fontSize: isLaptop ? '12px' : '14px',
                    padding: isLaptop ? '3px 5px' : '5px 8px',
                    height: isLaptop ? '28px' : '36px'
                  }}
                />
              </div>
              <div className="date-input-group">
                <label style={{ 
                  fontSize: isLaptop ? '12px' : '13px',
                  marginBottom: isLaptop ? '3px' : '6px'
                }}>
                  {t('dateRange.endDate')}
                </label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={handleEndDateChange}
                  style={{ 
                    fontSize: isLaptop ? '12px' : '14px',
                    padding: isLaptop ? '3px 5px' : '5px 8px',
                    height: isLaptop ? '28px' : '36px'
                  }}
                />
              </div>
            </div>
          </div>
          
          <div className="picker-actions" style={{ 
            marginTop: isLaptop ? '10px' : '15px',
            gap: isLaptop ? '6px' : '8px'
          }}>
            <button 
              className="cancel-button"
              onClick={() => {
                cancelTempFilters();
                setShowPicker(false);
              }}
              disabled={!hasUnappliedChanges}
              style={{
                fontSize: isLaptop ? '12px' : '13px',
                padding: isLaptop ? '3px 8px' : '5px 12px',
                height: isLaptop ? '26px' : '32px'
              }}
            >
              {t('dateRange.cancel')}
            </button>
            <button 
              className="apply-button"
              onClick={() => {
                applyTempFilters();
                setShowPicker(false);
              }}
              style={{
                fontSize: isLaptop ? '12px' : '13px',
                padding: isLaptop ? '3px 8px' : '5px 12px',
                height: isLaptop ? '26px' : '32px'
              }}
            >
              {t('dateRange.apply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;